import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Form, Image } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faMapMarker } from '@fortawesome/free-solid-svg-icons';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import { useAuth } from '../contexts/AuthContext';
import { ucfirst } from '../utils/utils';
import config from '../config/config';
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext';

const Access = () => {
    const { t } = useTranslation();
    const { activeLanguage } = useLanguage();
    const { restoData, setRestoData } = useResto();
    const { setIsLoading } = useWebsite();
    const { isModeAdmin } = useMode();
    const { token } = useAuth();

    // ✅ SAFE fallback
    const availableLanguages = restoData?.availableLanguages || [];

    const { apiUrl } = config;
    const apiPath = `${apiUrl}resto`;

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editedAccesss, setEditedAccesss] = useState({});

    const mapRef = useRef(null);
    const markerRef = useRef(null);

    // ----------------------------
    // GOOGLE MAP
    // ----------------------------
    useEffect(() => {
        if (!restoData || !mapRef.current) return;
        if (!window.google) return;

        const map = new window.google.maps.Map(mapRef.current, {
            center: {
                lat: Number(restoData.gpsx) || 0,
                lng: Number(restoData.gpsy) || 0
            },
            zoom: config.mapZoom,
        });

        const marker = new window.google.maps.Marker({
            position: {
                lat: Number(restoData.gpsx) || 0,
                lng: Number(restoData.gpsy) || 0
            },
            map: map,
            title: restoData.label || '',
            icon: '/marker.png',
            draggable: isModeAdmin
        });

        markerRef.current = marker;

        if (isModeAdmin) {
            marker.addListener('dragend', handleMarkerDragEnd);
        }

    }, [restoData, isModeAdmin]);

    const handleMarkerDragEnd = async () => {
        if (!markerRef.current) return;

        const newPosition = markerRef.current.getPosition();

        try {
            const response = await axios.put(
                `${apiPath}/${restoData?.website}`,
                {
                    gpsx: newPosition.lat(),
                    gpsy: newPosition.lng()
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);

        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }
    };

    // ----------------------------
    // EDIT MODAL
    // ----------------------------
    const handleOpenEditModal = () => {
        setEditedAccesss(restoData || {});
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
    };

    const handleEditSubmit = async () => {
        setIsLoading(true);

        try {
            const response = await axios.put(
                `${apiPath}/${restoData?.website}`,
                {
                    ...restoData,
                    ...editedAccesss
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);
            handleCloseEditModal();
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');

        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }

        setIsLoading(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        setEditedAccesss(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    // ----------------------------
    // RENDER
    // ----------------------------
    return (
        <div>

            <h2>
                <FontAwesomeIcon icon={faMapMarker} /> {t('ACCESS')}

                {isModeAdmin && (
                    <Button
                        variant="primary"
                        className="btn-sm"
                        style={{ marginLeft: '10px' }}
                        onClick={handleOpenEditModal}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </Button>
                )}
            </h2>

            {!isModeAdmin && (
                <p>
                    {restoData?.[`access${ucfirst(activeLanguage)}`] || ''}
                </p>
            )}

            {isModeAdmin && (
                <>
                    {availableLanguages.map(language => (
                        <div key={language}>
                            <p>
                                <Image
                                    src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                    className="flag-label"
                                    alt={language}
                                />
                                {' '}
                                {restoData?.[`access${ucfirst(language)}`] || ''}
                            </p>
                        </div>
                    ))}
                </>
            )}

            <Modal
                show={editModalOpen}
                onHide={handleCloseEditModal}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('EDIT')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {availableLanguages.map(language => (
                            <div key={language}>
                                <Form.Group
                                    controlId={`editAccess${ucfirst(language)}`}
                                >
                                    <Form.Label>
                                        <Image
                                            src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                            className="flag-label"
                                            alt={language}
                                        />
                                    </Form.Label>

                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name={`access${ucfirst(language)}`}
                                        value={
                                            editedAccesss[
                                                `access${ucfirst(language)}`
                                            ] || ''
                                        }
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </Form>
                </Modal.Body>

                <Modal.Footer className="justify-content-center">
                    <Button
                        variant="secondary"
                        onClick={handleCloseEditModal}
                    >
                        {t('CANCEL')}
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleEditSubmit}
                    >
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div style={{ height: '400px', width: '100%' }}>
                <div
                    ref={mapRef}
                    style={{ height: '100%', width: '100%' }}
                />
            </div>

        </div>
    );
};

export default Access;